import {useCallback} from "react";
import {HStack, Icon, IconButton} from "@chakra-ui/react";
import {ChevronDownIcon, ChevronUpIcon} from "@heroicons/react/20/solid";
import {useKeyboardShortcuts} from "../hooks/keyboardShortcuts";
import {Tooltip} from "./ui/tooltip";

export type NavDirection = "prev" | "next";

/** Segmented previous/next pair for a drawer header, also bound to Alt+↑ / Alt+↓.
 * `canPrev`/`canNext` gate the hotkeys as well as the buttons. */
export const DrawerNavButtons = ({
  onNavigate,
  canPrev,
  canNext,
  prevLabel,
  nextLabel,
  prevTooltip = prevLabel,
  nextTooltip = nextLabel,
}: {
  onNavigate: (direction: NavDirection) => void;
  canPrev: boolean;
  canNext: boolean;
  prevLabel: string;
  nextLabel: string;
  /** Defaults to the aria-label; pass these to name the hotkey too. */
  prevTooltip?: string;
  nextTooltip?: string;
}) => {
  const navigateByHotkey = useCallback(
    (direction: NavDirection) => {
      if (direction === "prev" ? !canPrev : !canNext) return;
      // Fields commit on blur, but the hotkey doesn't move focus and they persist
      // across the swap, so the in-progress edit would be discarded. Blur first, as
      // clicking a button would, to flush it before navigating.
      (document.activeElement as HTMLElement | null)?.blur();
      onNavigate(direction);
    },
    [canPrev, canNext, onNavigate],
  );

  useKeyboardShortcuts(
    {ArrowUp: () => navigateByHotkey("prev"), ArrowDown: () => navigateByHotkey("next")},
    {alt: true},
  );

  return (
    <HStack gap={0}>
      <Tooltip content={prevTooltip}>
        <IconButton
          variant="outline"
          size="sm"
          bg="white"
          _hover={{bg: "gray.50"}}
          borderRightRadius="0"
          aria-label={prevLabel}
          onClick={() => onNavigate("prev")}
          disabled={!canPrev}
        >
          <Icon>
            <ChevronUpIcon />
          </Icon>
        </IconButton>
      </Tooltip>
      <Tooltip content={nextTooltip}>
        <IconButton
          variant="outline"
          size="sm"
          bg="white"
          _hover={{bg: "gray.50"}}
          borderLeftRadius="0"
          marginLeft="-1px"
          aria-label={nextLabel}
          onClick={() => onNavigate("next")}
          disabled={!canNext}
        >
          <Icon>
            <ChevronDownIcon />
          </Icon>
        </IconButton>
      </Tooltip>
    </HStack>
  );
};
